import { useState, useEffect, useCallback } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import {
  IconBell,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronRight as IconChevronRightSm,
} from '@tabler/icons-react';
import type { NotificationQueueItem, NotificationType } from '../types';
import { listNotifications, type NotificationStatusFilter } from '../services/notificationsService';
import { getUserNames } from '../services/usersService';
import { triggerNotificationDispatch } from '../services/fireRiskService';

export default function NotificationsPage() {
  const [items, setItems] = useState<NotificationQueueItem[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<NotificationStatusFilter>('all');
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageHistory, setPageHistory] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>(
    []
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadItems = useCallback(
    async (cursor: QueryDocumentSnapshot<DocumentData> | null = null) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listNotifications(statusFilter, '', cursor);
        setItems(result.items);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);

        // Resolve user names
        const uids = [...new Set(result.items.map(i => i.userId))];
        if (uids.length > 0) {
          const names = await getUserNames(uids);
          setUserNames(prev => ({ ...prev, ...names }));
        }
      } catch (err) {
        setError('Failed to load notifications.');
        console.error('Error loading notifications:', err);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    setPageHistory([]);
    setLastDoc(null);
    loadItems(null);
  }, [statusFilter, loadItems]);

  function handleNextPage() {
    setPageHistory(h => [...h, lastDoc]);
    loadItems(lastDoc);
  }

  function handlePreviousPage() {
    const prev = pageHistory[pageHistory.length - 1] ?? null;
    setPageHistory(h => h.slice(0, -1));
    loadItems(prev);
  }

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  function renderTypeBadge(type?: NotificationType) {
    switch (type ?? 'fire_risk') {
      case 'fire_risk':
        return <span className="badge bg-danger text-white">Fire Risk</span>;
      case 'forum_new_post':
        return <span className="badge bg-azure text-white">Forum Post</span>;
      case 'forum_direct_reply':
        return <span className="badge bg-azure text-white">Forum Reply</span>;
      case 'forum_new_topic':
        return <span className="badge bg-azure text-white">Forum Topic</span>;
    }
  }

  function renderExpandedDetail(item: NotificationQueueItem) {
    const t = item.type ?? 'fire_risk';

    if (t === 'fire_risk') {
      const changes = item.municipalityChanges ?? [];
      if (changes.length === 0) return null;
      return (
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Municipality</th>
                <th>District</th>
                <th>Forecast</th>
                <th>Risk change</th>
                <th>Direction</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((mc, idx) => (
                <tr key={idx}>
                  <td>{mc.municipalityName}</td>
                  <td className="text-muted">{mc.districtName}</td>
                  <td>
                    <span className="badge bg-secondary text-white">{mc.forecastType}</span>
                  </td>
                  <td>
                    {mc.oldRisk} → {mc.newRisk}
                  </td>
                  <td>
                    {mc.direction === 'increase' ? (
                      <span className="badge bg-danger text-white">↑ increase</span>
                    ) : (
                      <span className="badge bg-success text-white">↓ decrease</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const fd = item.forumData;
    if (!fd) return null;
    return (
      <dl className="row mb-0 small">
        <dt className="col-sm-2">Room</dt>
        <dd className="col-sm-10">{fd.roomName}</dd>
        <dt className="col-sm-2">Topic</dt>
        <dd className="col-sm-10">{fd.topicTitle}</dd>
        <dt className="col-sm-2">Author</dt>
        <dd className="col-sm-10">{fd.authorName}</dd>
        {fd.postSnippet && (
          <>
            <dt className="col-sm-2">Post</dt>
            <dd className="col-sm-10 text-muted fst-italic">&ldquo;{fd.postSnippet}&rdquo;</dd>
          </>
        )}
      </dl>
    );
  }

  const currentPage = pageHistory.length + 1;

  async function handleTriggerDispatch() {
    try {
      setDispatching(true);
      setError(null);
      setSuccessMessage(null);
      const result = await triggerNotificationDispatch();
      if (result.success) {
        setSuccessMessage(
          `Notifications dispatched — processed: ${result.processed}, sent: ${result.sent}, failed: ${result.failed}`
        );
        loadItems(null);
      } else {
        setError(result.message ?? 'Dispatch failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dispatch notifications');
    } finally {
      setDispatching(false);
    }
  }

  return (
    <>
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">Users</div>
            <h2 className="page-title">Notifications</h2>
          </div>
          <div className="col-auto ms-auto">
            <button
              className="btn btn-outline-secondary"
              onClick={handleTriggerDispatch}
              disabled={dispatching}
              title="Send any queued notifications that are due"
            >
              {dispatching ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Dispatching...
                </>
              ) : (
                <>
                  <IconBell className="icon me-2" />
                  Send Notifications
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="alert alert-success mt-3" role="alert">
          {successMessage}
        </div>
      )}

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value as NotificationStatusFilter);
                  setPageHistory([]);
                  setLastDoc(null);
                }}
              >
                <option value="all">All</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card mt-3">
        {loading ? (
          <div className="card-body">
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="card-body">
            <div className="empty">
              <div className="empty-icon">
                <IconBell size={48} stroke={1.5} />
              </div>
              <p className="empty-title">No notifications found</p>
              <p className="empty-subtitle text-muted">Try changing the filters.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Scheduled For</th>
                    <th>Status</th>
                    <th>Sent At</th>
                    <th>Type</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <>
                      <tr key={item.id}>
                        <td>
                          {userNames[item.userId] || (
                            <span className="text-muted">{item.userId.slice(0, 8)}</span>
                          )}
                        </td>
                        <td>{formatDate(item.scheduledFor)}</td>
                        <td>
                          {item.sent ? (
                            <span className="badge bg-success text-white">Sent</span>
                          ) : (
                            <span className="badge bg-warning text-white">Pending</span>
                          )}
                        </td>
                        <td>
                          {item.sentAt ? (
                            formatDate(item.sentAt)
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{renderTypeBadge(item.type)}</td>
                        <td>
                          <button
                            className="btn btn-icon"
                            title="Expand details"
                            onClick={() =>
                              setExpandedId(prev => (prev === item.id ? null : (item.id ?? null)))
                            }
                          >
                            {expandedId === item.id ? (
                              <IconChevronDown size={16} />
                            ) : (
                              <IconChevronRightSm size={16} />
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedId === item.id && (
                        <tr key={`${item.id}-expanded`}>
                          <td colSpan={6} className="bg-light p-3">
                            {renderExpandedDetail(item) ?? (
                              <span className="text-muted">No details available.</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-footer d-flex align-items-center">
              <p className="m-0 text-muted">Page {currentPage}</p>
              <ul className="pagination m-0 ms-auto">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <IconChevronLeft size={16} />
                    prev
                  </button>
                </li>
                <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNextPage} disabled={!hasMore}>
                    next
                    <IconChevronRight size={16} />
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}
